/**
 * RelayService — Web browser ↔ boclaw-web RelayHub ↔ Desktop client.
 *
 * Manages a `/ws/relay-app` WebSocket connection to relay requests/events
 * between the Web frontend and a user's Desktop client. Stream events from
 * the Desktop are translated into Redux dispatches so the existing Cowork UI
 * (messages, permissions, status) works transparently.
 *
 * Key differences from the main `/ws` connection (webAdapter):
 *   Main WS format:  { event: 'cowork.stream.message', data: { sessionId, message } }
 *   Relay WS format: { type: 'event', action: 'stream.message', payload: { sessionId, message }, ... }
 *
 * Desktop sets origin='app' for all relay sessions. We force-override to 'desktop'
 * in Redux so the UI shows the correct badge.
 */

import { store } from '../store';
import { getAuthSessionScope } from './authSessionScope';
import {
  addMessage, updateMessageContent, updateMessageMetadata, updateSessionStatus,
  enqueuePendingPermission, dequeuePendingPermission, setStreaming, setCurrentSession, setCurrentSessionId,
  upsertDesktopSession, patchDesktopSession, deleteSession, updateSessionModelId,
} from '../store/slices/coworkSlice';
import type {
  CoworkIMMetadata,
  CoworkPermissionMode,
  CoworkSession,
  CoworkSessionSummary,
} from '../types/cowork';
// coding 用命名空间导入，避免与 cowork 的同名 action（updateMessageContent/setStreaming）冲突
import * as codingActions from '../store/slices/codingSlice';
import type { CodingSessionSummary } from '../coding/types';

// ─── Constants ───
const RELAY_PROTOCOL_VERSION = 1;
const REQUEST_TIMEOUT_MS = 30_000;
const HEARTBEAT_INTERVAL_MS = 25_000;
const FILE_TRANSFER_TIMEOUT_MS = 120_000;
const FILE_TRANSFER_MAX_BYTES = 20 * 1024 * 1024;

// ─── Types ───

export interface RelayClientInfo {
  clientId: string;
  deviceName: string;
  platform: string;       // 'darwin' | 'win32' | 'linux'
  appVersion: string;
  productType: 'bomate' | 'bocoder' | 'legacy';
  online: boolean;
  registeredAt: number;
  lastSeenAt: number;
}

interface RelayMessage {
  id: string;
  type: 'request' | 'response' | 'event' | 'ping' | 'pong';
  action: string;
  payload?: any;
  timestamp: number;
  version: number;
  error?: { code: string; message: string } | null;
  targetClientId?: string;
  sourceClientId?: string;
}

// ─── Virtual web client guard ───
// The server registers each browser's main /ws as a virtual relay client
// (clientId `web-{userId}`, platform 'web') to power cloud coding execution.
// It is an execution environment, NOT a desktop device — desktop discovery and
// desktop-session association must always skip it, otherwise cloud sandbox
// sessions returned by its `session.list` localRequest handler get wrongly
// stamped origin='desktop' (shown as 桌面端 in the UI).

function isVirtualWebClient(clientId?: string | null, platform?: string | null): boolean {
  return Boolean(clientId?.startsWith('web-')) || platform === 'web';
}

// ─── targetClientId mapping (in-memory only) ───
// sessionId → clientId, for routing continue/stop/get calls to the right
// desktop. Deliberately NOT persisted anywhere (no localStorage): the web
// session list for a desktop must always be a live mirror of that desktop's
// own list, rebuilt from scratch (fetchClients + discoverDesktopSessions) on
// every load/reconnect — never something remembered from a previous visit or
// a previously logged-in account on the same browser.

const targetClientMap = new Map<string, string>();

function setTargetClient(sessionId: string, clientId: string): void {
  targetClientMap.set(sessionId, clientId);
}

function removeTargetClient(sessionId: string): void {
  targetClientMap.delete(sessionId);
}

export function getTargetClient(sessionId: string): string | null {
  return targetClientMap.get(sessionId) ?? null;
}

export interface DesktopSessionSummary extends CoworkSessionSummary {
  origin: 'desktop';
  clientId?: string;
  /** 所属桌面设备名(发现/关联时从 relay 客户端在线信息读取);多桌面登录时用于区分来源设备。 */
  deviceName?: string;
}

/** 清掉本次页面会话里累积的 sessionId→clientId 映射。登出时调用,避免同一
 *  浏览器 tab 不刷新页面直接切账号时,残留的映射把新账号错误路由到旧账号
 *  关联过的桌面客户端。没有 localStorage 需要清——本来就不落盘。 */
export function clearRelayCache(): void {
  targetClientMap.clear();
  relayService.disconnect();
}

function normalizeIMMetadata(value: unknown): CoworkIMMetadata | undefined {
  let metadata = value;
  if (typeof metadata === 'string') {
    try {
      metadata = JSON.parse(metadata);
    } catch {
      return undefined;
    }
  }
  if (!metadata || typeof metadata !== 'object') return undefined;
  const candidate = metadata as Partial<CoworkIMMetadata>;
  if (typeof candidate.platform !== 'string') return undefined;
  return candidate as CoworkIMMetadata;
}

function toDesktopSummary(session: any): DesktopSessionSummary | null {
  if (!session?.id) return null;
  const now = Date.now();
  return {
    id: session.id,
    title: session.title || 'New Session',
    status: session.status || 'idle',
    pinned: Boolean(session.pinned),
    modelId: session.modelId ?? session.model ?? null,
    permissionMode: session.permissionMode ?? null,
    imMetadata: normalizeIMMetadata(session.imMetadata),
    origin: 'desktop',
    createdAt: session.createdAt || now,
    updatedAt: session.updatedAt || now,
  } as DesktopSessionSummary;
}

function toDesktopSession(session: any, messages?: any[]): CoworkSession | null {
  const summary = toDesktopSummary(session);
  if (!summary) return null;
  return {
    ...session,
    ...summary,
    claudeSessionId: session.claudeSessionId ?? null,
    cwd: session.cwd || session.workingDirectory || '',
    systemPrompt: session.systemPrompt || '',
    executionMode: session.executionMode || 'sandbox',
    activeSkillIds: session.activeSkillIds || [],
    messages: messages ?? session.messages ?? [],
  } as CoworkSession;
}

// ─── Platform label helper ───

export function platformLabel(platform: string): string {
  switch (platform) {
    case 'darwin': return 'macOS';
    case 'win32': return 'Windows';
    case 'linux': return 'Linux';
    default: return platform;
  }
}

// ─── RelayService class ───

class RelayService {
  private ws: WebSocket | null = null;
  private clients = new Map<string, RelayClientInfo>();
  private pendingRequests = new Map<string, {
    resolve: (payload: any) => void;
    reject: (error: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  }>();
  private listeners = new Set<() => void>();
  private connectionPromise: Promise<void> | null = null;
  private connectionEpoch = 0;
  private connectedIdentity: string | null = null;
  private sessionSelection = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  /** Session IDs initiated by this tab — used to filter broadcast stream events. */
  private ownedSessionIds = new Set<string>();
  /** Coding session IDs this tab is viewing/owns — filters coding.stream.* broadcasts. */
  private codingOwnedSessionIds = new Set<string>();
  private fileTransfers = new Map<string, {
    clientId: string;
    size: number;
    mimeType: string;
    chunks: string[];
    total: number | null;
    resolve: (blob: Blob) => void;
    reject: (error: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  }>();
  private startingFileTransfers = new Map<string, number>();
  private earlyFileEvents = new Map<string, { clientId: string; events: RelayMessage[]; timer: ReturnType<typeof setTimeout> }>();

  // ── Connection management ──

  /** 同一登录周期复用连接 Promise；并发调用必须等到握手完成后才能发送。 */
  ensureConnected(): Promise<void> {
    const identity = this.identity();
    if (this.connectedIdentity && this.connectedIdentity !== identity) this.disconnect();
    if (this.ws?.readyState === WebSocket.OPEN) return Promise.resolve();
    if (this.connectionPromise) return this.connectionPromise;
    const epoch = ++this.connectionEpoch;
    this.connectedIdentity = identity;
    const pending = this.connect(epoch, identity);
    this.connectionPromise = pending;
    return pending;
  }

  /** 续期复用连接与在途请求，真正重新登录时由统一登录周期阻断旧消息。 */
  private identity(): string {
    return getAuthSessionScope();
  }

  /** 发现和 WebSocket 握手绑定同一个账号周期；旧连接不得修改新连接状态。 */
  private async connect(epoch: number, identity: string): Promise<void> {
    const isCurrent = () => epoch === this.connectionEpoch && identity === this.identity();
    try {
      // 1. REST: fetch initial client list
      await this.fetchClients();
      if (!isCurrent()) throw new Error('Account changed');

      // 2. WS: connect to relay-app endpoint
      const token = localStorage.getItem('boclaw_token');
      const userId = localStorage.getItem('boclaw_user_id');
      if (!token) throw new Error('Not authenticated');

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const params = new URLSearchParams();
      params.set('token', token);
      if (userId) params.set('userId', userId);
      const url = `${protocol}//${window.location.host}/ws/relay-app?${params}`;

      await new Promise<void>((resolve, reject) => {
        const connection = new WebSocket(url);
        this.ws = connection;
        connection.onopen = () => {
          if (!isCurrent()) { connection.close(); reject(new Error('Account changed')); return; }
          console.log('[RelayService] WS connected');
          this.startHeartbeat();
          resolve();
        };
        connection.onmessage = (event) => { if (isCurrent() && this.ws === connection) this.handleWsMessage(event); };
        connection.onclose = () => {
          reject(new Error('WebSocket closed'));
          if (this.ws !== connection) return;
          console.log('[RelayService] WS closed');
          this.stopHeartbeat();
          this.rejectAllPending('WebSocket closed');
          this.ws = null;
        };
        connection.onerror = (err) => {
          console.error('[RelayService] WS error:', err);
          reject(new Error('WebSocket connection failed'));
        };
      });

      // fetchClients() returns peers that were already online before this Web
      // tab connected. Those peers will not emit another peer.online event, so
      // proactively request their session lists once the Relay socket is ready.
      // Otherwise the sidebar stays empty until a later session event happens.
      if (!isCurrent()) throw new Error('Account changed');
      await this.discoverDesktopSessions();
    } catch (error) {
      if (isCurrent() && this.ws?.readyState !== WebSocket.OPEN) {
        this.ws?.close();
        this.ws = null;
      }
      throw error;
    } finally {
      if (epoch === this.connectionEpoch) this.connectionPromise = null;
    }
  }

  disconnect(): void {
    this.connectionEpoch += 1;
    this.sessionSelection += 1;
    this.connectedIdentity = null;
    this.connectionPromise = null;
    this.clients.clear();
    this.codingOwnedSessionIds.clear();
    this.notifyListeners();
    this.stopHeartbeat();
    this.rejectAllPending('Disconnected');
    if (this.ws) {
      const connection = this.ws;
      this.ws = null;
      connection.close();
    }
    this.ownedSessionIds.clear();
  }

  // ── Client discovery ──

  async fetchClients(): Promise<RelayClientInfo[]> {
    const identity = this.identity();
    const token = localStorage.getItem('boclaw_token');
    const resp = await fetch('/api/relay/clients', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'x-boclaw-user-id': localStorage.getItem('boclaw_user_id') || '',
        'x-boclaw-user-role': localStorage.getItem('boclaw_user_role') || '',
      },
    });
    if (!resp.ok) throw new Error(`Failed to fetch relay clients: ${resp.status}`);
    const { clients } = await resp.json();
    if (identity !== this.identity()) throw new Error('Account changed');
    this.clients.clear();
    for (const c of clients) {
      this.clients.set(c.clientId, c);
    }
    this.notifyListeners();
    return clients;
  }

  getOnlineClients(): RelayClientInfo[] {
    return Array.from(this.clients.values()).filter(c => c.online);
  }

  getAllClients(): RelayClientInfo[] {
    return Array.from(this.clients.values());
  }

  /** Subscribe to client list changes. Returns unsubscribe function. */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  // ── Relay requests ──

  async sendRequest(action: string, payload: unknown, targetClientId: string): Promise<any> {
    // 云端环境（虚拟 web 客户端）走 REST 旁路，不依赖 relay WS。会话在 server 本地执行，
    // 流事件经主 /ws 回推（见 initCloudEventBridge）。
    if (targetClientId.startsWith('web-') && action.startsWith('coding.')) {
      return this.cloudRequest(action, payload);
    }

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || this.connectedIdentity !== this.identity()) {
      throw new Error('Relay WebSocket not connected');
    }

    const id = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const msg: RelayMessage = {
      id,
      type: 'request',
      action,
      payload,
      timestamp: Date.now(),
      version: RELAY_PROTOCOL_VERSION,
      targetClientId,
    };

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingRequests.delete(id);
        reject(new Error(`Relay request timeout: ${action}`));
      }, REQUEST_TIMEOUT_MS);

      this.pendingRequests.set(id, { resolve, reject, timer });
      this.ws!.send(JSON.stringify(msg));
    });
  }

  /** 云端环境 coding 请求经 REST 旁路（POST /api/coding/request），返回结果即 relay payload。 */
  private async cloudRequest(action: string, payload: unknown): Promise<any> {
    this.initCloudEventBridge();
    const token = localStorage.getItem('boclaw_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-boclaw-user-id': localStorage.getItem('boclaw_user_id') || '',
      'x-boclaw-user-role': localStorage.getItem('boclaw_user_role') || '',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const resp = await fetch('/api/coding/request', {
      method: 'POST',
      headers,
      body: JSON.stringify({ action, payload }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
    return data;
  }

  /** 订阅主 /ws 上的 coding.* 流事件，路由到 handleCodingEvent（与 relay 路径同一套逻辑）。只装一次。 */
  private cloudEventBridgeReady = false;
  private initCloudEventBridge(): void {
    if (this.cloudEventBridgeReady) return;
    const on = (window as any).electron?.ipcRenderer?.on;
    if (typeof on !== 'function') return;
    const CODING_EVENTS = [
      'coding.session.created', 'coding.session.updated', 'coding.session.deleted',
      'coding.stream.message', 'coding.stream.messageUpdate', 'coding.stream.permission',
      'coding.stream.permissionResolved', 'coding.stream.statusChange', 'coding.stream.queued',
      'coding.stream.complete', 'coding.stream.error',
    ];
    for (const action of CODING_EVENTS) {
      on(action, (data: any) => {
        this.handleCodingEvent({ type: 'event', action, payload: data } as RelayMessage);
      });
    }
    this.cloudEventBridgeReady = true;
  }

  // ── Session operations ──

  async startSession(targetClientId: string, opts: {
    /** 同云端会话契约，实际持久化由目标桌面完成。 */
    scenario?: import('../../shared/sessionScenario').SessionScenario | null;
    prompt: string;
    title?: string;
    systemPrompt?: string;
    activeSkillIds?: string[];
    modelId?: string;
    permissionMode?: CoworkPermissionMode;
    /** 桌面端工作目录（用户经 CwdPickerModal 选定）；不传则由桌面端用自己的默认目录。 */
    cwd?: string;
    /** Files already staged on the desktop through file.upload. */
    attachmentPaths?: string[];
  }): Promise<{ session: any }> {
    const result = await this.sendRequest('session.start', { ...opts, interactionSurface: 'web' }, targetClientId) as { session: any };
    if (result?.session?.id) {
      this.ownedSessionIds.add(result.session.id);
      this.associateDesktopSession(result.session, targetClientId);
    }
    return result;
  }

  /** Upload a browser-selected file directly to the target desktop. */
  async uploadFile(
    targetClientId: string,
    sessionId: string,
    fileName: string,
    dataBase64: string,
    cwd?: string,
  ): Promise<{ path: string }> {
    return await this.sendRequest(
      'file.upload',
      { sessionId, fileName, dataBase64, cwd },
      targetClientId,
    ) as { path: string };
  }

  /** Read a desktop image as a downscaled base64 payload for Web preview. */
  async readImage(
    targetClientId: string,
    filePath: string,
    maxDimension = 1600,
    quality = 80,
  ): Promise<{ content: string; mimeType: string; originalSize?: number }> {
    return await this.sendRequest(
      'file.readImage',
      { path: filePath, maxDimension, quality },
      targetClientId,
    ) as { content: string; mimeType: string; originalSize?: number };
  }

  /** Read a desktop attachment (up to the desktop's 5MB read limit). */
  async readFile(
    targetClientId: string,
    filePath: string,
  ): Promise<{
    path: string;
    encoding: 'utf-8' | 'base64';
    content: string;
    size: number;
    mimeType: string;
  }> {
    return await this.sendRequest(
      'file.read',
      { path: filePath },
      targetClientId,
    ) as {
      path: string;
      encoding: 'utf-8' | 'base64';
      content: string;
      size: number;
      mimeType: string;
    };
  }

  async downloadFile(targetClientId: string, filePath: string): Promise<Blob> {
    this.startingFileTransfers.set(targetClientId, (this.startingFileTransfers.get(targetClientId) ?? 0) + 1);
    let result: { transferId: string; size: number; mimeType?: string };
    try {
      result = await this.sendRequest('file.download', { path: filePath }, targetClientId);
    } finally {
      const count = (this.startingFileTransfers.get(targetClientId) ?? 1) - 1;
      if (count > 0) this.startingFileTransfers.set(targetClientId, count);
      else this.startingFileTransfers.delete(targetClientId);
    }
    if (!result?.transferId || !Number.isFinite(result.size) || result.size < 0 || result.size > FILE_TRANSFER_MAX_BYTES) {
      throw new Error('Invalid desktop file transfer response');
    }
    if (this.fileTransfers.has(result.transferId)) throw new Error('Duplicate desktop file transfer');

    return new Promise<Blob>((resolve, reject) => {
      const transferId = result.transferId;
      const timer = setTimeout(() => this.failFileTransfer(transferId, 'Desktop file transfer timed out'), FILE_TRANSFER_TIMEOUT_MS);
      this.fileTransfers.set(transferId, {
        clientId: targetClientId, size: result.size, mimeType: result.mimeType || 'application/octet-stream',
        chunks: [], total: null, resolve, reject, timer,
      });
      const early = this.earlyFileEvents.get(transferId);
      if (early) {
        clearTimeout(early.timer);
        this.earlyFileEvents.delete(transferId);
        for (const event of early.events) this.handleFileEvent(event);
      }
    });
  }

  async deleteSession(targetClientId: string, sessionId: string): Promise<void> {
    await this.sendRequest('session.delete', { sessionId }, targetClientId);
  }

  associateDesktopSession(session: any, clientId: string): DesktopSessionSummary | null {
    // Never associate sessions with the virtual web client — those are cloud
    // sessions executed server-side, not desktop sessions.
    if (isVirtualWebClient(clientId)) return null;
    const summary = toDesktopSummary(session);
    if (!summary) return null;
    const deviceName = this.clients.get(clientId)?.deviceName || undefined;
    const withClient = { ...summary, clientId, ...(deviceName ? { deviceName } : {}) };
    setTargetClient(summary.id, clientId);
    store.dispatch(upsertDesktopSession(withClient));
    return withClient;
  }

  /** 解析桌面会话所属设备名:只看在线 relay 客户端的实时信息——桌面离线后
   *  就不再假装知道设备名,与"web 端不存任何缓存数据"的原则一致。 */
  getDesktopSessionDeviceName(sessionId: string): string | null {
    const clientId = getTargetClient(sessionId);
    if (!clientId) return null;
    return this.clients.get(clientId)?.deviceName || null;
  }

  async discoverDesktopSessions(): Promise<void> {
    for (const client of this.getOnlineClients()) {
      if (isVirtualWebClient(client.clientId, client.platform)) continue;
      await this.discoverDesktopClient(client.clientId);
    }
  }

  /** 用户手动触发的"刷新桌面会话"入口:重新拉取在线客户端列表,再对每个在线
   *  桌面重新同步会话——过时/已不在桌面本地存在的条目会被按 discoverDesktopClient
   *  的对比逻辑从 Redux 里清掉。用于自助排查"web 端显示的桌面会话跟桌面本身
   *  对不上"而不必刷新整个页面。 */
  async refreshDesktopSessions(): Promise<void> {
    await this.fetchClients();
    await this.discoverDesktopSessions();
  }

  private async discoverDesktopClient(clientId: string): Promise<void> {
    const identity = this.identity();
    // The virtual web client answers `session.list` with ALL of the user's
    // cloud sessions (boot.ts localRequest) — never treat those as desktop.
    if (isVirtualWebClient(clientId)) return;
    try {
      const result = await this.listSessions(clientId);
      if (identity !== this.identity()) return;
      const sessionIds = new Set<string>();
      for (const session of result?.sessions || []) {
        const summary = this.associateDesktopSession(session, clientId);
        if (summary) sessionIds.add(summary.id);
      }
      // 桌面这边已经没有的会话(被删除/重启后本来就不该再出现的孤儿条目),
      // 从 Redux 里直接摘掉——没有本地缓存要同步清,Redux 就是唯一状态。
      for (const existing of store.getState().cowork.sessions) {
        if (existing.origin === 'desktop' && getTargetClient(existing.id) === clientId && !sessionIds.has(existing.id)) {
          this.removeDesktopSession(existing.id);
        }
      }
    } catch (error) {
      console.warn('[RelayService] Failed to discover desktop sessions:', error);
    }
  }

  async continueSession(
    targetClientId: string, sessionId: string, prompt: string,
    systemPrompt?: string, modelId?: string, activeSkillIds?: string[],
    scenario?: import('../../shared/sessionScenario').SessionScenario | null,
  ): Promise<void> {
    this.ownedSessionIds.add(sessionId);
    await this.sendRequest('session.continue', { sessionId, prompt, systemPrompt, modelId, activeSkillIds, scenario, interactionSurface: 'web' }, targetClientId);
  }

  async setSessionModel(targetClientId: string, sessionId: string, model: string): Promise<void> {
    const result = await this.sendRequest('session.setModel', { sessionId, model }, targetClientId) as {
      session?: { model?: string };
    };
    store.dispatch(updateSessionModelId({ sessionId, modelId: result.session?.model ?? model }));
  }

  /** 读取 Desktop Cowork 默认权限模式及其支持的枚举值。 */
  async getCoworkConfig(targetClientId: string): Promise<{
    model: string | null;
    availableModels: Array<{ id: string; name: string; provider?: string }>;
    permissionMode: CoworkPermissionMode;
    permissionModes: CoworkPermissionMode[];
  }> {
    const result = await this.sendRequest('config.get', {}, targetClientId) as {
      config?: {
        model?: string | null;
        availableModels?: Array<{ id: string; name: string; provider?: string }>;
        permissionMode?: CoworkPermissionMode;
        permissionModes?: CoworkPermissionMode[];
      };
    };
    return {
      model: result.config?.model ?? null,
      availableModels: Array.isArray(result.config?.availableModels) ? result.config.availableModels : [],
      permissionMode: result.config?.permissionMode ?? 'auto',
      permissionModes: result.config?.permissionModes ?? ['default', 'auto', 'bypassPermissions'],
    };
  }

  /** 热更新单个 Desktop Cowork 会话权限，不修改 Desktop 全局配置。 */
  async setPermissionMode(
    targetClientId: string,
    sessionId: string,
    permissionMode: CoworkPermissionMode,
  ): Promise<void> {
    await this.sendRequest(
      'session.setPermissionMode',
      { sessionId, permissionMode },
      targetClientId,
    );
  }

  async stopSession(targetClientId: string, sessionId: string): Promise<void> {
    await this.sendRequest('session.stop', { sessionId }, targetClientId);
  }

  async getSession(targetClientId: string, sessionId: string): Promise<{ session: any; messages: any[] }> {
    return await this.sendRequest('session.get', { sessionId }, targetClientId) as any;
  }

  async setResultFeedback(targetClientId: string, sessionId: string, messageId: string, messageTimestamp: number, feedback: 'helpful' | 'not_helpful' | null): Promise<void> {
    await this.sendRequest('session.feedback', { sessionId, messageId, messageTimestamp, feedback, interactionSurface: 'web' }, targetClientId);
  }

  async getResultOpinionTags(targetClientId: string): Promise<Array<{ code: string; name: string }>> {
    const result = await this.sendRequest('session.feedbackTags', {}, targetClientId) as { tags?: Array<{ code: string; name: string }> };
    return result.tags || [];
  }

  async submitResultOpinion(targetClientId: string, sessionId: string, messageId: string, messageTimestamp: number, tagCodes: string[], content: string): Promise<Record<string, unknown>> {
    const result = await this.sendRequest('session.feedbackOpinion', { sessionId, messageId, messageTimestamp, tagCodes, content }, targetClientId) as { metadata?: Record<string, unknown> };
    return result.metadata || {};
  }

  async loadDesktopSession(targetClientId: string, sessionId: string): Promise<CoworkSession | null> {
    const selection = ++this.sessionSelection;
    store.dispatch(setCurrentSessionId(sessionId));
    const identity = this.identity();
    const result = await this.getSession(targetClientId, sessionId);
    if (selection !== this.sessionSelection || identity !== this.identity() || store.getState().cowork.currentSessionId !== sessionId) return null;
    const session = toDesktopSession(result?.session, result?.messages);
    if (!session) return null;
    this.claimSession(session.id);
    this.associateDesktopSession(session, targetClientId);
    store.dispatch(setCurrentSession(session));
    store.dispatch(setStreaming(session.status === 'running'));
    return session;
  }

  forgetDesktopSession(sessionId: string): void {
    this.removeDesktopSession(sessionId);
  }

  async respondToPermission(targetClientId: string, requestId: string, result: unknown): Promise<void> {
    await this.sendRequest('permission.respond', { requestId, result }, targetClientId);
  }

  async listSessions(targetClientId: string): Promise<{ sessions: any[] }> {
    return await this.sendRequest('session.list', {}, targetClientId) as any;
  }

  /** 查询指定桌面客户端的本地异常上报状态。 */
  async getErrorReportStatus(targetClientId: string, errorId: string): Promise<any> {
    return await this.sendRequest('errorReport.status', { errorId }, targetClientId) as any;
  }

  /** 让指定桌面客户端使用本地持久队列手工补报异常。 */
  async retryErrorReport(targetClientId: string, errorId: string): Promise<any> {
    return await this.sendRequest('errorReport.retry', { errorId }, targetClientId) as any;
  }

  /** Check whether a session is owned by this tab (for external callers). */
  isOwnedSession(sessionId: string): boolean {
    return this.ownedSessionIds.has(sessionId);
  }

  /** Claim ownership of a session (e.g. after page reload). */
  claimSession(sessionId: string): void {
    this.ownedSessionIds.add(sessionId);
  }

  // ── 桌面端定时任务 —— 网页端隔着 relay 看/控本机的定时任务 ──
  //
  // 只有查看与控制，没有新建/编辑：桌面端任务要填工作目录、执行模式这些本机概念，
  // 网页端的表单没有这些字段。桌面端版本过旧时这些 action 会回 UNKNOWN_ACTION，
  // 由调用方翻译成「该设备暂不支持」。

  async listScheduledTasks(targetClientId: string): Promise<{ tasks: any[] }> {
    return await this.sendRequest('scheduledTask.list', {}, targetClientId) as any;
  }

  async getScheduledTask(targetClientId: string, taskId: string): Promise<{ task: any }> {
    return await this.sendRequest('scheduledTask.get', { taskId }, targetClientId) as any;
  }

  async deleteScheduledTask(targetClientId: string, taskId: string): Promise<{ deleted: boolean }> {
    return await this.sendRequest('scheduledTask.delete', { taskId }, targetClientId) as any;
  }

  async toggleScheduledTask(
    targetClientId: string, taskId: string, enabled: boolean,
  ): Promise<{ task: any; warning: string | null }> {
    return await this.sendRequest('scheduledTask.toggle', { taskId, enabled }, targetClientId) as any;
  }

  async runScheduledTaskNow(targetClientId: string, taskId: string): Promise<{ started: boolean }> {
    return await this.sendRequest('scheduledTask.runNow', { taskId }, targetClientId) as any;
  }

  async stopScheduledTask(targetClientId: string, taskId: string): Promise<{ stopped: boolean }> {
    return await this.sendRequest('scheduledTask.stop', { taskId }, targetClientId) as any;
  }

  async listScheduledTaskRuns(
    targetClientId: string, taskId: string, limit?: number, offset?: number,
  ): Promise<{ runs: any[] }> {
    return await this.sendRequest('scheduledTask.listRuns', { taskId, limit, offset }, targetClientId) as any;
  }

  async listAllScheduledTaskRuns(
    targetClientId: string, limit?: number, offset?: number,
  ): Promise<{ runs: any[] }> {
    return await this.sendRequest('scheduledTask.listAllRuns', { limit, offset }, targetClientId) as any;
  }

  // ── Coding（编程）会话操作 —— 远程控制 desktop 的 CodingRunner ──

  /** 标记本 tab 订阅某个 coding 会话的流（打开已存在会话/刷新后调用）。 */
  claimCodingSession(sessionId: string): void {
    this.codingOwnedSessionIds.add(sessionId);
  }

  async listCodingSessions(targetClientId: string): Promise<{ sessions: any[] }> {
    return await this.sendRequest('coding.session.list', {}, targetClientId) as any;
  }

  async getCodingSession(
    targetClientId: string, sessionId: string, offset = 0, limit = 50,
  ): Promise<{ session: any; messages: any[]; pagination?: any }> {
    this.codingOwnedSessionIds.add(sessionId);
    return await this.sendRequest('coding.session.get', { sessionId, offset, limit }, targetClientId) as any;
  }

  async startCodingSession(targetClientId: string, opts: {
    prompt: string;
    cwd?: string;
    model?: string;
    permissionMode?: string;
    title?: string;
    sandboxProfileId?: string | null;
    sandboxImageId?: string | null;
    gitRepoUrl?: string;
    gitBranch?: string;
    gitUsername?: string;
    gitPassword?: string;
  }): Promise<{ session: any }> {
    const result = await this.sendRequest('coding.session.start', opts, targetClientId) as { session: any };
    if (result?.session?.id) {
      this.codingOwnedSessionIds.add(result.session.id);
      setTargetClient(result.session.id, targetClientId);
    }
    return result;
  }

  async continueCodingSession(targetClientId: string, sessionId: string, prompt: string): Promise<void> {
    this.codingOwnedSessionIds.add(sessionId);
    await this.sendRequest('coding.session.continue', { sessionId, prompt }, targetClientId);
  }

  async stopCodingSession(targetClientId: string, sessionId: string): Promise<void> {
    await this.sendRequest('coding.session.stop', { sessionId }, targetClientId);
  }

  async deleteCodingSession(targetClientId: string, sessionId: string): Promise<void> {
    await this.sendRequest('coding.session.delete', { sessionId }, targetClientId);
  }

  /** 重命名会话标题（desktop 需支持 coding.session.rename，阶段B 补全）。 */
  async renameCodingSession(targetClientId: string, sessionId: string, title: string): Promise<void> {
    await this.sendRequest('coding.session.rename', { sessionId, title }, targetClientId);
  }

  async setCodingModel(targetClientId: string, sessionId: string, model: string): Promise<void> {
    await this.sendRequest('coding.session.setModel', { sessionId, model }, targetClientId);
  }

  async setCodingPermissionMode(targetClientId: string, sessionId: string, permissionMode: string): Promise<void> {
    await this.sendRequest('coding.session.setPermissionMode', { sessionId, permissionMode }, targetClientId);
  }

  async respondCodingPermission(
    targetClientId: string,
    sessionId: string,
    requestId: string,
    decision: 'allow' | 'deny' | 'allow_always',
    extra?: { reason?: string; updatedInput?: unknown },
  ): Promise<void> {
    await this.sendRequest('coding.permission.respond', {
      sessionId, requestId, decision, ...extra,
    }, targetClientId);
  }

  async getCodingConfig(targetClientId: string): Promise<{
    availableModels: any[]; defaultModel: string | null; permissionModes: string[]; defaultCwd: string;
  }> {
    return await this.sendRequest('coding.config.get', {}, targetClientId) as any;
  }

  // ── 工作目录选择器（远程浏览 desktop 目录树，与 cowork 共用 cwd.* 通道）──
  // 注意：action 不以 'coding.' 开头，不会命中 sendRequest 的云端 REST 旁路 —— 选择器仅桌面会话使用，走 relay WS。

  /** 拉桌面常用目录预设 + 默认 cwd（home / defaultCwd / presets[{label,path}]）。 */
  async suggestCwd(targetClientId: string): Promise<{
    home: string; defaultCwd: string; presets: { label: string; path: string }[];
  }> {
    return await this.sendRequest('cwd.suggest', {}, targetClientId) as any;
  }

  /** 列出某目录下的子目录（desktop 侧只列目录、过滤隐藏、最多 200 条、限制在 home 子树内）。 */
  async listCwd(targetClientId: string, path: string): Promise<{
    path: string; parent: string | null; entries: { name: string; path: string }[]; truncated: boolean;
  }> {
    return await this.sendRequest('cwd.list', { path }, targetClientId) as any;
  }

  // ── Internal: WS message handling ──

  private handleWsMessage(event: MessageEvent): void {
    let msg: RelayMessage;
    try {
      msg = JSON.parse(event.data);
    } catch {
      console.warn('[RelayService] Failed to parse WS message');
      return;
    }

    // pong — heartbeat response, ignore
    if (msg.type === 'pong') return;

    // response — match pending request
    if (msg.type === 'response') {
      const pending = this.pendingRequests.get(msg.id);
      if (pending) {
        clearTimeout(pending.timer);
        this.pendingRequests.delete(msg.id);
        if (msg.error) {
          pending.reject(new Error(`${msg.error.code}: ${msg.error.message}`));
        } else {
          pending.resolve(msg.payload);
        }
      }
      return;
    }

    // event — stream events or peer events
    if (msg.type === 'event') {
      if (msg.action === 'file.chunk' || msg.action === 'file.complete' || msg.action === 'file.cancelled') {
        this.handleFileEvent(msg);
      } else if (msg.action.startsWith('coding.')) {
        this.handleCodingEvent(msg);
      } else if (msg.action.startsWith('stream.')) {
        this.handleStreamEvent(msg);
      } else if (msg.action === 'peer.online' || msg.action === 'peer.offline') {
        this.handlePeerEvent(msg);
      } else if (msg.action === 'session.created') {
        this.handleSessionCreated(msg);
      } else if (msg.action === 'session.updated') {
        this.handleSessionUpdated(msg);
      } else if (msg.action === 'session.deleted') {
        this.handleSessionDeleted(msg);
      }
    }
  }

  private failFileTransfer(transferId: string, reason: string): void {
    const transfer = this.fileTransfers.get(transferId);
    if (!transfer) return;
    clearTimeout(transfer.timer);
    this.fileTransfers.delete(transferId);
    transfer.reject(new Error(reason));
  }

  private handleFileEvent(msg: RelayMessage): void {
    const payload = msg.payload as { transferId?: string; index?: number; total?: number; data?: string } | undefined;
    const transferId = payload?.transferId;
    const clientId = msg.sourceClientId;
    if (!transferId || !clientId) return;
    const transfer = this.fileTransfers.get(transferId);
    if (!transfer) {
      if (!this.startingFileTransfers.has(clientId)) return;
      let early = this.earlyFileEvents.get(transferId);
      if (!early) {
        if (this.earlyFileEvents.size >= 4) return;
        const timer = setTimeout(() => this.earlyFileEvents.delete(transferId), REQUEST_TIMEOUT_MS);
        early = { clientId, events: [], timer };
        this.earlyFileEvents.set(transferId, early);
      }
      if (early.clientId === clientId && early.events.length < 81 &&
        (msg.action !== 'file.chunk' || (typeof payload.data === 'string' && payload.data.length <= 400_000))) {
        early.events.push(msg);
      }
      return;
    }
    if (transfer.clientId !== clientId) return;

    switch (msg.action) {
      case 'file.chunk': {
        const { index, total, data } = payload;
        if (!Number.isInteger(index) || !Number.isInteger(total) || !data ||
          index! < 0 || total! < 1 || total! > 80 || index! >= total! || data.length > 400_000 ||
          (transfer.total !== null && transfer.total !== total)) {
          this.failFileTransfer(transferId, 'Invalid desktop file chunk');
          return;
        }
        transfer.total = total!;
        if (transfer.chunks[index!] !== undefined) {
          this.failFileTransfer(transferId, 'Duplicate desktop file chunk');
          return;
        }
        transfer.chunks[index!] = data;
        break;
      }
      case 'file.complete': {
        if (transfer.size > 0 && (transfer.total === null || transfer.chunks.filter(Boolean).length !== transfer.total)) {
          this.failFileTransfer(transferId, 'Desktop file transfer is incomplete');
          return;
        }
        try {
          const parts = transfer.chunks.map((chunk) => Uint8Array.from(atob(chunk), (char) => char.charCodeAt(0)));
          const blob = new Blob(parts, { type: transfer.mimeType });
          if (blob.size !== transfer.size) throw new Error('Desktop file size mismatch');
          clearTimeout(transfer.timer);
          this.fileTransfers.delete(transferId);
          transfer.resolve(blob);
        } catch (error) {
          this.failFileTransfer(transferId, error instanceof Error ? error.message : 'Invalid desktop file data');
        }
        break;
      }
      case 'file.cancelled':
        this.failFileTransfer(transferId, 'Desktop file transfer cancelled');
        break;
    }
    if (this.fileTransfers.has(transferId)) {
      clearTimeout(transfer.timer);
      transfer.timer = setTimeout(() => this.failFileTransfer(transferId, 'Desktop file transfer timed out'), FILE_TRANSFER_TIMEOUT_MS);
    }
  }

  /**
   * 处理 coding.* relay 事件 → 派发到 codingSlice。
   *
   * stream.* 消息流按 codingOwnedSessionIds 过滤（多 tab 隔离）；
   * session.* 列表事件不过滤（同账号所有端共享会话列表）。
   */
  private handleCodingEvent(msg: RelayMessage): void {
    const p = (msg.payload ?? {}) as any;
    const sessionId = p.sessionId as string | undefined;

    switch (msg.action) {
      // ── 会话列表事件（不按 owned 过滤）──
      case 'coding.session.created': {
        if (p.session) {
          // 注册流订阅（云端 REST 旁路下，created 经主 /ws 先于 HTTP 响应到达也能确保后续 stream 不被过滤）
          if (p.session.id) this.codingOwnedSessionIds.add(p.session.id);
          store.dispatch(codingActions.upsertSession(p.session as CodingSessionSummary));
        }
        return;
      }
      case 'coding.session.updated': {
        if (!sessionId) return;
        const patch: any = { sessionId };
        if (p.status !== undefined) patch.status = p.status;
        if (p.model !== undefined) patch.model = p.model;
        if (p.permissionMode !== undefined) patch.permissionMode = p.permissionMode;
        if (p.updatedAt !== undefined) patch.updatedAt = p.updatedAt;
        store.dispatch(codingActions.patchSession(patch));
        return;
      }
      case 'coding.session.deleted': {
        if (sessionId) store.dispatch(codingActions.removeSession(sessionId));
        return;
      }
    }

    // ── 流式事件（按 owned 过滤）──
    if (!sessionId || !this.codingOwnedSessionIds.has(sessionId)) return;

    switch (msg.action) {
      case 'coding.stream.message': {
        if (p.message) {
          store.dispatch(codingActions.upsertMessage({ sessionId, message: p.message }));
          store.dispatch(codingActions.setStreaming(true));
        }
        break;
      }
      case 'coding.stream.messageUpdate': {
        if (p.messageId && p.content !== undefined) {
          store.dispatch(codingActions.updateMessageContent({
            sessionId, messageId: p.messageId, content: p.content,
          }));
        }
        break;
      }
      case 'coding.stream.permission': {
        const req = (p.request ?? {}) as any;
        const requestId = req.requestId ?? req.id;
        if (!requestId) break;
        store.dispatch(codingActions.enqueuePermission({
          sessionId,
          requestId,
          toolName: req.toolName ?? req.tool ?? req.name ?? 'unknown',
          toolInput: req.toolInput ?? req.input ?? {},
          toolUseId: req.toolUseId ?? null,
          supportsAllowAlways: !!req.supportsAllowAlways,
        }));
        break;
      }
      case 'coding.stream.permissionResolved': {
        store.dispatch(codingActions.clearPermission({ sessionId, requestId: p.requestId }));
        break;
      }
      case 'coding.stream.statusChange': {
        if (p.status !== undefined) {
          store.dispatch(codingActions.setStatus({ sessionId, status: p.status }));
          store.dispatch(codingActions.setStreaming(p.status === 'running'));
        }
        break;
      }
      case 'coding.stream.queued': {
        store.dispatch(codingActions.setQueuedCount({ sessionId, count: p.count ?? 0 }));
        break;
      }
      case 'coding.stream.complete': {
        store.dispatch(codingActions.setStreaming(false));
        break;
      }
      case 'coding.stream.error': {
        store.dispatch(codingActions.setStreaming(false));
        if (p.recoverable === false) {
          store.dispatch(codingActions.setStatus({ sessionId, status: 'error' }));
        }
        break;
      }
    }
  }

  /**
   * Translate Relay stream events into Redux actions.
   *
   * Relay format:  { type:'event', action:'stream.message', payload:{ sessionId, message } }
   * Main WS:       { event:'cowork.stream.message', data:{ sessionId, message } }
   * These are completely different — we manually dispatch to Redux here.
   */
  private handleStreamEvent(msg: RelayMessage): void {
    const payload = msg.payload as any;
    if (!payload) return;
    const sessionId = payload.sessionId as string;
    if (!sessionId) return;

    // Desktop sessions discovered from this user's client should stay live across tabs.
    if (getTargetClient(sessionId) !== msg.sourceClientId) {
      console.log('[RelayService] Ignoring stream event for non-owned session:', sessionId);
      return;
    }

    switch (msg.action) {
      case 'stream.messageMetadata': {
        if (payload.messageId && payload.metadata) {
          store.dispatch(updateMessageMetadata({ sessionId, messageId: payload.messageId, metadata: payload.metadata }));
        }
        break;
      }
      case 'stream.message': {
        const message = payload.message;
        if (message) {
          store.dispatch(addMessage({ sessionId, message }));
          if (message.type === 'user') {
            store.dispatch(updateSessionStatus({ sessionId, status: 'running' }));
          }
        }
        break;
      }

      case 'stream.messageUpdate': {
        const { messageId, content } = payload;
        if (messageId && content !== undefined) {
          store.dispatch(updateMessageContent({ sessionId, messageId, content }));
        }
        break;
      }

      case 'stream.permission': {
        const request = payload.request;
        if (request) {
          store.dispatch(enqueuePendingPermission({
            sessionId,
            toolName: request.toolName,
            toolInput: request.toolInput,
            requestId: request.requestId,
            toolUseId: request.toolUseId ?? null,
          }));
        }
        break;
      }

      case 'stream.permissionResolved': {
        if (payload.requestId && store.getState().cowork.pendingPermissions.some(
          (permission) => permission.sessionId === sessionId && permission.requestId === payload.requestId
        )) {
          store.dispatch(dequeuePendingPermission({ requestId: payload.requestId }));
        }
        break;
      }

      case 'stream.complete': {
        this.clearSessionPermissions(sessionId);
        const status = payload.status === 'stopped' || payload.status === 'interrupted'
          ? 'idle'
          : payload.status === 'failed' ? 'error' : 'completed';
        store.dispatch(updateSessionStatus({ sessionId, status }));
        if (store.getState().cowork.currentSessionId === sessionId) store.dispatch(setStreaming(false));
        break;
      }

      case 'stream.error': {
        this.clearSessionPermissions(sessionId);
        store.dispatch(updateSessionStatus({ sessionId, status: 'error' }));
        if (store.getState().cowork.currentSessionId === sessionId) store.dispatch(setStreaming(false));
        break;
      }
    }
  }

  private clearSessionPermissions(sessionId: string): void {
    for (const permission of store.getState().cowork.pendingPermissions) {
      if (permission.sessionId === sessionId) {
        store.dispatch(dequeuePendingPermission({ requestId: permission.requestId }));
      }
    }
  }

  private handlePeerEvent(msg: RelayMessage): void {
    const payload = msg.payload as any;
    if (!payload) return;

    const { clientId, deviceName, platform, appVersion, productType } = payload;
    if (!clientId) return;

    if (msg.action === 'peer.online') {
      this.clients.set(clientId, {
        clientId,
        deviceName: deviceName || '',
        platform: platform || '',
        appVersion: appVersion || '',
        productType: productType || 'legacy',
        online: true,
        registeredAt: Date.now(),
        lastSeenAt: Date.now(),
      });
      // Skip desktop-session discovery for the virtual web client (fires on
      // every main /ws reconnect of this same browser).
      if (!isVirtualWebClient(clientId, platform)) {
        void this.discoverDesktopClient(clientId);
      }
    } else if (msg.action === 'peer.offline') {
      const existing = this.clients.get(clientId);
      if (existing) {
        existing.online = false;
        existing.lastSeenAt = Date.now();
      }
      for (const session of store.getState().cowork.sessions) {
        if (session.origin !== 'desktop' || getTargetClient(session.id) !== clientId) continue;
        this.clearSessionPermissions(session.id);
        if (session.status === 'running') {
          store.dispatch(updateSessionStatus({ sessionId: session.id, status: 'idle' }));
          if (store.getState().cowork.currentSessionId === session.id) store.dispatch(setStreaming(false));
        }
      }
    }
    this.notifyListeners();
  }

  private handleSessionCreated(msg: RelayMessage): void {
    const clientId = msg.sourceClientId;
    if (!clientId) return;
    this.associateDesktopSession((msg.payload as any)?.session, clientId);
  }

  private handleSessionUpdated(msg: RelayMessage): void {
    const payload = msg.payload as any;
    if (!payload?.sessionId) return;
    const sessionId = payload.sessionId as string;
    const patch: Partial<Omit<CoworkSessionSummary, 'id' | 'origin'>> = {};
    if (payload.title !== undefined) patch.title = payload.title;
    if (payload.status !== undefined) patch.status = payload.status;
    if (payload.pinned !== undefined) patch.pinned = payload.pinned;
    if (payload.model !== undefined || payload.modelId !== undefined) {
      patch.modelId = payload.modelId ?? payload.model ?? null;
    }
    if (payload.permissionMode !== undefined) patch.permissionMode = payload.permissionMode;
    if (payload.imMetadata !== undefined) patch.imMetadata = normalizeIMMetadata(payload.imMetadata);
    if (payload.createdAt !== undefined) patch.createdAt = payload.createdAt;
    if (payload.updatedAt !== undefined) patch.updatedAt = payload.updatedAt;

    const existing = store.getState().cowork.sessions.find((session) => session.id === sessionId);
    if (existing?.origin === 'desktop') {
      store.dispatch(patchDesktopSession({ sessionId, patch }));
    }
  }

  private handleSessionDeleted(msg: RelayMessage): void {
    const sessionId = (msg.payload as any)?.sessionId;
    if (typeof sessionId === 'string') this.removeDesktopSession(sessionId);
  }

  private removeDesktopSession(sessionId: string): void {
    this.ownedSessionIds.delete(sessionId);
    removeTargetClient(sessionId);
    store.dispatch(deleteSession(sessionId));
  }

  // ── Heartbeat ──

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        const ping: RelayMessage = {
          id: crypto.randomUUID?.() || `ping-${Date.now()}`,
          type: 'ping',
          action: '',
          payload: {},
          timestamp: Date.now(),
          version: RELAY_PROTOCOL_VERSION,
        };
        this.ws.send(JSON.stringify(ping));
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // ── Utility ──

  private notifyListeners(): void {
    this.listeners.forEach(fn => fn());
  }

  private rejectAllPending(reason: string): void {
    for (const [, pending] of this.pendingRequests) {
      clearTimeout(pending.timer);
      pending.reject(new Error(reason));
    }
    this.pendingRequests.clear();
    for (const transferId of this.fileTransfers.keys()) this.failFileTransfer(transferId, reason);
    for (const early of this.earlyFileEvents.values()) clearTimeout(early.timer);
    this.earlyFileEvents.clear();
    this.startingFileTransfers.clear();
  }
}

export const relayService = new RelayService();
